import type React from 'react';
import { useEffect, useState } from 'react';
import type { StepProps } from '../../types/onboarding.types';
import { SUGGESTIONS_MAP } from '../../utils/constants';
import Suggestions from '../shared/Suggestions';

const IndustriesStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  onBack,
  isLoading,
  error,
  selectedRole,
}) => {
  const [input, setInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setLocalError(null);
  }, []);

  const handleAddIndustry = (industry: string) => {
    const trimmed = industry.trim();
    if (!trimmed) {
      setLocalError('Please enter an industry');
      return;
    }

    if (data.industries.includes(trimmed)) {
      setLocalError('This industry has already been added');
      return;
    }

    onChange({ ...data, industries: [...data.industries, trimmed] });
    setInput('');
    setLocalError(null);
  };

  const handleRemoveIndustry = (industry: string) => {
    const updated = data.industries.filter((i) => i !== industry);
    onChange({ ...data, industries: updated });
    setLocalError(null);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setLocalError(null);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleAddIndustry(suggestion);
    setLocalError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIndustry(input);
    }
  };

  const handleSubmit = async () => {
    if (data.industries.length === 0) {
      setLocalError('Please add at least one industry');
      return;
    }

    const isValid = await validation(data);
    if (isValid) {
      setLocalError(null);
      onNext();
    } else {
      setLocalError('Please add at least one industry');
    }
  };

  const _showSuggestions = data.industries.length === 0 && !input.trim();

  return (
    <div className="space-y-6">
      {/* Suggestions */}
      <div className="mb-[28px]">
        <p className="flex items-center text-heading-4 font-paragraph mb-4 text-[#737982]">
          Suggestions
        </p>
        <Suggestions
          suggestions={SUGGESTIONS_MAP.Industries}
          onSuggestionClick={handleSuggestionClick}
          layout="default"
        />
      </div>

      {/* Input field for direct entry */}
      <div className="relative w-full max-w-[508px]">
        <input
          type="text"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Add industries"
          className="w-full max-w-[508px] text-body-text-large font-paragraph border border-border-gray rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 px-4 py-2 text-[#737982]"
        />
      </div>

      {/* Added Industries Display */}
      {data.industries.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Target Industries ({data.industries.length})
          </h3>
          <div className="flex flex-wrap gap-2 max-h-[250px] overflow-y-auto font-paragraph text-heading-4">
            {data.industries.map((industry, index) => (
              <div key={`${industry}-${index}`} className="flex items-center m-1">
                <div className="bg-primary text-white rounded-l-[4px] px-3 py-1 flex items-center">
                  {industry}
                </div>
                <button
                  onClick={() => handleRemoveIndustry(industry)}
                  className="bg-primary text-white rounded-r-md ml-[2px] px-[4px] py-1 flex items-center hover:bg-red-700 transition-colors"
                >
                  <XIcon className="w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || localError}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button onClick={handleSubmit} disabled={isLoading || data.industries.length === 0} />
      </div>
    </div>
  );
};

// Import XIcon from the UI library
import { XIcon } from '@commander/ui';

export default IndustriesStep;
